import type {
  FormatUnix,
  ProfileBooking,
  ProfileBranch,
  ProfileCourseDay,
} from './profileBooking.types';

export const WEEK_DAYS: Record<string, string> = {
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
  SU: 'Sunday',
};

const BOOKING_DATE_FORMAT = 'dddd Do of MMMM';
const COURSE_DATE_FORMAT = 'MMM Do';
const DEFAULT_TIME_FORMAT = 'HH:mm';
const COURSE_MODELS = ['course', 'courses'];

export function getBranchTimeFormat(branch: ProfileBranch | null | undefined): string {
  return branch?.configuration?.formats?.time || DEFAULT_TIME_FORMAT;
}

export function getEventName(booking: ProfileBooking): string {
  const model = booking.model_obj;
  const fallback = booking.type ?? '';
  if (!model || Object.keys(model).length === 0) {
    return fallback;
  }
  if (model.name) {
    return model.name;
  }
  if (model.first_name && model.last_name) {
    return `${model.first_name} ${model.last_name}`;
  }
  return fallback;
}

export function getEventDescription(booking: ProfileBooking): string {
  return booking.model_obj?.facility_obj?.name ?? '';
}

export function getDateFormated(booking: ProfileBooking, formatUnix: FormatUnix): string {
  return booking.time_start ? formatUnix(booking.time_start, BOOKING_DATE_FORMAT) : '';
}

export function getTimeFormated(
  booking: ProfileBooking,
  branch: ProfileBranch | null | undefined,
  formatUnix: FormatUnix
): string {
  const start = booking.time_start ? formatUnix(booking.time_start, getBranchTimeFormat(branch)) : '';
  return booking.duration ? `${start} - ${booking.duration} minutes` : start;
}

export function getCourseDateFormated(booking: ProfileBooking, formatUnix: FormatUnix): string {
  const firstSlot = booking.model_obj?.schedule?.[0];
  if (!firstSlot) {
    return '';
  }
  const start = firstSlot.start_date ? formatUnix(firstSlot.start_date, COURSE_DATE_FORMAT) : '';
  const end = firstSlot.end_date ? formatUnix(firstSlot.end_date, COURSE_DATE_FORMAT) : '';
  return end ? `${start} - ${end}` : start;
}

export function getCourseTime(
  day: ProfileCourseDay,
  branch: ProfileBranch | null | undefined,
  formatUnix: FormatUnix
): string {
  const format = getBranchTimeFormat(branch);
  const start = day.start_time ? formatUnix(day.start_time, format) : '';
  const end = day.end_time ? formatUnix(day.end_time, format) : '';
  return `${start} to ${end}`;
}

export function getDayOfTheWeek(day: ProfileCourseDay): string {
  return (day.days && WEEK_DAYS[day.days]) || '';
}

export type GroupedProfileBookings<TBooking extends ProfileBooking> = {
  /** Newest first, matching the Angular `_.reverse(bookingsGrouped.true)`. */
  upcoming: TBooking[];
  /** API order. */
  past: TBooking[];
  isEmpty: boolean;
};

/**
 * Splits bookings into upcoming and past around `nowUnix` (seconds). The
 * Angular version compared branch-local wall-clock strings; both sides were
 * rendered in the same timezone, so comparing the underlying instants is
 * equivalent. A booking starting exactly now counts as past, as before.
 */
export function groupBookings<TBooking extends ProfileBooking>(
  bookings: TBooking[] | null | undefined,
  nowUnix: number
): GroupedProfileBookings<TBooking> {
  const upcoming: TBooking[] = [];
  const past: TBooking[] = [];
  (bookings ?? []).forEach((booking) => {
    if ((booking.time_start ?? 0) > nowUnix) {
      upcoming.push(booking);
    } else {
      past.push(booking);
    }
  });
  upcoming.reverse();
  return { upcoming, past, isEmpty: upcoming.length === 0 && past.length === 0 };
}

function isEmptyBooking(booking: ProfileBooking | null | undefined): booking is null | undefined {
  return !booking || Object.keys(booking).length === 0;
}

export function showCancelButton(isUpcoming: boolean, booking: ProfileBooking | null | undefined): boolean {
  if (!isUpcoming || isEmptyBooking(booking)) {
    return false;
  }
  return booking.status !== 'CANCELED' && !COURSE_MODELS.includes(booking.model ?? '');
}

export function cancelOrWaitlistLocale(booking: ProfileBooking | null | undefined): string {
  return booking?.status === 'WAITING' ? 'WEB_LEAVE_WAITLIST' : 'WEB_CANCEL';
}
